import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { Input } from '../../../components';
import { GeneralService, UserService } from '../../../services';
import { sanitizeToNumbers, fromPhotos, classNames } from '../../../utils';
import { PasswordActive, PasswordInactive } from '../../../assets/icons';

const ModalCreateClient = ({ onClose, onSuccess, initialData }) => {
  const { form, imagenPreview, isLoading, onChange, handleImageChange, removeImage, selects, selectSuggestion, submit } = useForm({ initialData, onClose, onSuccess });
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef();
  const isEdit = !!initialData?.id;

  return (
    <div className="w-[48rem] bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4">
        <h2 className="text-lg font-bold text-text">{isEdit ? 'Editar' : 'Crear'} Cliente</h2>
        <button
          type="button"
          className="p-1.5 rounded-full hover:bg-gray5 transition-colors"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-text">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex justify-center px-6 mb-4">
        <div
          className="flex flex-col items-center justify-center border-2 border-dashed border-gray6 rounded-2xl p-6 bg-gray5 hover:bg-gray-100 transition-colors cursor-pointer min-h-[180px] w-48 relative overflow-hidden"
          onClick={() => fileInputRef.current?.click()}
        >
          {imagenPreview ? (
            <>
              <img src={imagenPreview} alt="Vista previa" className="absolute inset-0 w-full h-full object-cover rounded-2xl" />
              <button
                type="button"
                className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-md hover:bg-gray-100 transition-colors z-10"
                onClick={e => { e.stopPropagation(); removeImage(); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-text">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <div className="bg-white p-3 rounded-full shadow-sm mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-text">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                </svg>
              </div>
              <span className="text-text font-semibold text-sm">Subir foto</span>
              <span className="text-text text-xs mt-1">JPG, JPEG o PNG</span>
            </>
          )}
          <input
            type="file"
            accept=".jpg,.jpeg,.png"
            ref={fileInputRef}
            onChange={handleImageChange}
            className="hidden"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 px-6">
        <div className="col-span-2">
          <p className="text-lg font-semibold text-black">Datos de la empresa</p>
        </div>
        <Input
          label="Nombre"
          value={form.company?.name || ''}
          onValueChange={v => onChange('company.name', v)}
        />
        <div>
          <p className="mb-1 text-text text-sm">Rif - C.I.</p>
          <div className="flex items-center border border-gray6 bg-white h-10 w-full rounded-full overflow-hidden">
            <select
              className="h-full bg-transparent outline-none text-sm px-4 min-w-[120px] border-r border-gray6"
              value={form.company?.document_type || ''}
              onChange={e => onChange('company.document_type', e.target.value)}
            >
              <option value="">Seleccionar</option>
              {selects.documentTypes.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
            <input
              type="text"
              className="flex-1 h-full outline-none border-none bg-transparent px-4 text-sm"
              value={form.company?.document || ''}
              onChange={e => onChange('company.document', sanitizeToNumbers(e.target.value))}
              placeholder="Número de documento"
            />
          </div>
        </div>
        {/* <Input
          label="Correo"
          value={form.company?.email || ''}
          onValueChange={v => onChange('company.email', v)}
        />
        <Input
          label="Teléfono"
          value={form.company?.phone || ''}
          onValueChange={v => onChange('company.phone', v)}
        /> */}
        <Input
          className={{ base: 'col-span-2' }}
          label="Dirección"
          value={form.company?.address_house || ''}
          onValueChange={v => onChange('company.address_house', v)}
        />

        <div className="col-span-2 mt-2">
          <p className="text-lg font-semibold text-black">Ubicación</p>
        </div>

        <div id="map-create-client" className="max-h-96 aspect-square" />

        <PlacesAutocomplete
          value={form._address || ''}
          onChange={v => onChange('_address', v)}
          onSelect={selectSuggestion}
          searchOptions={{ componentRestrictions: { country: ['ve'] } }}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
            <div className="w-full relative">
              <div className="w-full">
                <div className="flex items-center gap-x-4 border border-gray6 bg-white h-10 w-full rounded-full px-5 py-3">
                  <input
                    placeholder="Buscar lugar"
                    name="_address"
                    {...getInputProps({
                      className: 'flex-1 outline-none border-none active:outline-none bg-transparent w-full',
                    })}
                  />
                </div>
              </div>
              <div className="p-2 mt-2 bg-white absolute z-10 w-full shadow-md rounded-lg empty:hidden">
                {loading && <div className="loading">Cargando...</div>}
                {suggestions.map(suggestion => {
                  const className = classNames('cursor-pointer bg-white hover:font-normal hover:bg-black/10', suggestion.active ? 'bg-gray6 font-semibold' : '');
                  return (
                    <div {...getSuggestionItemProps(suggestion, { className })}>
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </PlacesAutocomplete>

        <div className="col-span-2 mt-2">
          <p className="text-lg font-semibold text-black">Persona de contacto</p>
        </div>
        <Input
          label="Nombre"
          value={form.name || ''}
          onValueChange={v => onChange('name', v)}
        />
        <Input
          label="Apellido"
          value={form.lastname || ''}
          onValueChange={v => onChange('lastname', v)}
        />
        <Input
          label="Cargo"
          value={form.work_position || ''}
          onValueChange={v => onChange('work_position', v)}
        />
        <Input
          label="Teléfono"
          value={form.phone || ''}
          onValueChange={v => onChange('phone', sanitizeToNumbers(v))}
          maxLength={11}
        />
        <Input
          label="Correo"
          value={form.email || ''}
          onValueChange={v => onChange('email', v)}
        />

        <div className="col-span-2 mt-2">
          <p className="text-lg font-semibold text-black">Contraseña</p>
        </div>
        <Input
          label="Contraseña"
          type={showPassword ? 'text' : 'password'}
          value={form.password || ''}
          onValueChange={v => onChange('password', v)}
          append={
            <button
              type="button"
              tabIndex="-1"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400"
            >
              <img src={showPassword ? PasswordActive : PasswordInactive} className="w-6 h-6" alt="" />
            </button>
          }
        />
        <Input
          label="Repetir contraseña"
          type={showPassword ? 'text' : 'password'}
          value={form.password_confirmation || ''}
          onValueChange={v => onChange('password_confirmation', v)}
          append={
            <button
              type="button"
              tabIndex="-1"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400"
            >
              <img src={showPassword ? PasswordActive : PasswordInactive} className="w-6 h-6" alt="" />
            </button>
          }
        />
      </div>

      <div className="flex justify-center mt-2 px-6 py-4 gap-3">
        <button
          type="button"
          className="px-6 py-2 bg-gray-200 text-text font-semibold rounded-full hover:bg-gray-300 transition-colors text-sm"
          onClick={onClose}
          disabled={isLoading}
        >
          Cerrar
        </button>
        <button
          type="button"
          className="px-6 py-2 bg-red text-white font-semibold rounded-full hover:bg-gray-800 transition-colors text-sm disabled:opacity-50"
          onClick={submit}
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
};

const useForm = ({ initialData, onClose, onSuccess }) => {
  const isEdit = !!initialData?.id;
  const map = useRef();

  const getInitial = (data) => ({
    id: data?.id || '',
    company: {
      name: data?.person?.company?.name || '',
      document_type: data?.person?.company?.document_type || '',
      document: data?.person?.company?.document || '',
      email: data?.person?.company?.email || '',
      phone: data?.person?.company?.phone || '',
      address_house: data?.person?.company?.address_house || '',
      latitude: data?.person?.company?.latitude || '',
      longitude: data?.person?.company?.longitude || '',
    },
    name: data?.person?.name || '',
    lastname: data?.person?.lastname || '',
    work_position: data?.person?.work_position || '',
    phone: data?.person?.phone || '',
    email: data?.email || '',
    password: '',
    password_confirmation: '',
    photo: data?.person?.photo || '',
    hasFile: true,
    _address: '',
  });

  const [form, setForm] = useState(() => getInitial(initialData));
  const [selects, setSelects] = useState({
    documentTypes: [],
  });
  const [imagenPreview, setImagenPreview] = useState(() =>
    initialData?.person?.photo ? fromPhotos(initialData.person.photo) : null
  );
  const [isLoading, setIsLoading] = useState(false);

  const getSelects = async () => {
    try {
      const response = await GeneralService.getDocumentTypes();
      setSelects({
        documentTypes: response,
      });

    } catch (error) {
      setSelects({ states: [] });
    }
  };

  const setMap = () => {
    map.current = new window.google.maps.Map(document.getElementById('map-create-client'), {
      zoom: 13,
      center: new window.google.maps.LatLng(form.company?.latitude || 10.503703739685555, form.company?.longitude || -66.91201470063201),
      draggable: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      scaleControl: false,
      rotateControl: false
    });

    const marker = new window.google.maps.Marker({
      position: map.current.getCenter(),
      map: map.current,
      title: '',
      animation: window.google.maps.Animation.DROP,
      draggable: false,
    });

    window.google.maps.event.addListener(map.current, 'center_changed', () => {
      marker.setPosition(map.current.getCenter());
    });
  };

  const selectSuggestion = async (address) => {
    try {
      const results = await geocodeByAddress(address);
      if (!results.length) return;

      const latLng = await getLatLng(results[0]);
      map.current.setCenter(latLng);
      onChange('company.address_house', address);

    } catch (error) {
      console.error('Error', error)
    }
  };

  const onChange = (target, value) => {
    if (target.startsWith('company.')) {
      const key = target.split('.')[1];
      setForm(s => ({ ...s, company: { ...s.company, [key]: value } }));
    } else {
      setForm(s => ({ ...s, [target]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(s => ({ ...s, photo: file }));
    setImagenPreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setForm(s => ({ ...s, photo: null }));
    setImagenPreview(null);
  };

  const isValidForm = () => {
    const onError = (msg) => {
      toast.error(msg);
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.photo)
      return onError('La foto es requerida');

    if (!form.company.name.trim())
      return onError('El nombre de la empresa es requerido');

    if (!form.company.document_type)
      return onError('El tipo de documento es requerido');

    if (!form.company.document)
      return onError('El número de documento es requerido');

    if (form.company.document.length < 5)
      return onError('El número de documento debe tener al menos 5 caracteres');

    if (!form.company.address_house)
      return onError('La dirección es requerida');

    // if (form.company.email && !emailRegex.test(form.company.email))
    //   return onError('El correo de la empresa no es válido');

    // if (!form.company.phone)
    //   return onError('El teléfono de la empresa es requerido');

    // if (form.company.phone.length < 7)
    //   return onError('El teléfono de la empresa debe tener al menos 7 dígitos');

    if (!form.name.trim())
      return onError('El nombre del contacto es requerido');

    if (!form.lastname.trim())
      return onError('El apellido del contacto es requerido');

    if (!form.phone)
      return onError('El teléfono del contacto es requerido');

    if (form.phone.length < 7)
      return onError('El teléfono del contacto debe tener al menos 7 dígitos');

    if (!form.email.trim())
      return onError('El correo electrónico de inicio de sesión es requerido');

    if (!emailRegex.test(form.email))
      return onError('El correo electrónico de inicio de sesión no es válido');

    if (!isEdit && !form.password)
      return onError('La contraseña es requerida');

    if (form.password && form.password.length < 6)
      return onError('La contraseña debe tener al menos 6 caracteres');

    if (form.password !== form.password_confirmation)
      return onError('Las contraseñas no coinciden');

    return true;
  };

  const submit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);
    try {
      const payload = {
        ...form,
        company: JSON.stringify({
          ...form.company,
          latitude: map.current.getCenter().lat(),
          longitude: map.current.getCenter().lng(),
        }),
      }

      if (initialData?.id) {
        await UserService.admin.updateClient(payload);
        toast.success('Cliente actualizado con éxito');
      } else {
        await UserService.admin.createClient(payload);
        toast.success('Cliente creado con éxito');
      }
      onSuccess?.();
      onClose?.();

    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSelects();
    setTimeout(() => setMap(), 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    form,
    handleImageChange,
    imagenPreview,
    isLoading,
    onChange,
    removeImage,
    selects,
    selectSuggestion,
    submit,
  };
};

export default ModalCreateClient;
